import { useState, useEffect } from "react";
import { getLegalPage, publishLegalPage } from "../../../api/legal-pages";
import type { LegalPageLanguage, LegalPageType } from "../../../types/legal-page";
import iconCheckCircle from "../../../assets/icon-check-circle.svg";

const TABS: { label: string; type: LegalPageType }[] = [
  { label: "Privacy Policy", type: "privacy_policy" },
  { label: "Cookie Policy", type: "cookie_policy" },
  { label: "Terms Of Use", type: "terms_of_use" },
  { label: "Legal Notice", type: "legal_notice" },
];

type LanguageMeta = { code: LegalPageLanguage; label: string; dir: "ltr" | "rtl" };

const LANGUAGES: readonly LanguageMeta[] = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
] as const;

const DEFAULT_LANGUAGE_META: LanguageMeta = LANGUAGES[0]!;

const EMPTY_TRANSLATIONS: Record<LegalPageLanguage, string> = { en: "", ar: "" };

function sameTranslations(
  a: Record<LegalPageLanguage, string>,
  b: Record<LegalPageLanguage, string>,
): boolean {
  return a.en === b.en && a.ar === b.ar;
}

export function LegalPagesPage() {
  const [activeTab, setActiveTab] = useState<LegalPageType>("privacy_policy");
  const [activeLanguage, setActiveLanguage] = useState<LegalPageLanguage>("en");
  const [translations, setTranslations] =
    useState<Record<LegalPageLanguage, string>>(EMPTY_TRANSLATIONS);
  const [originalTranslations, setOriginalTranslations] =
    useState<Record<LegalPageLanguage, string>>(EMPTY_TRANSLATIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);

  const hasChanges = !sameTranslations(translations, originalTranslations);
  const canPublish = hasChanges && translations.en.trim() !== "" && translations.ar.trim() !== "";
  const activeLabel = TABS.find((t) => t.type === activeTab)?.label ?? "";
  const activeLanguageMeta: LanguageMeta =
    LANGUAGES.find((l) => l.code === activeLanguage) ?? DEFAULT_LANGUAGE_META;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const page = await getLegalPage(activeTab);
        if (!cancelled) {
          const next: Record<LegalPageLanguage, string> = {
            en: page.translations?.en ?? "",
            ar: page.translations?.ar ?? "",
          };
          setTranslations(next);
          setOriginalTranslations(next);
        }
      } catch {
        if (!cancelled) {
          setTranslations(EMPTY_TRANSLATIONS);
          setOriginalTranslations(EMPTY_TRANSLATIONS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (!toastVisible) return;
    const timer = setTimeout(() => setToastVisible(false), 3000);
    return () => clearTimeout(timer);
  }, [toastVisible]);

  async function handlePublish() {
    if (!canPublish || saving) return;
    setSaving(true);
    try {
      const page = await publishLegalPage(activeTab, translations);
      const next: Record<LegalPageLanguage, string> = {
        en: page.translations?.en ?? "",
        ar: page.translations?.ar ?? "",
      };
      setTranslations(next);
      setOriginalTranslations(next);
      setToastVisible(true);
    } catch {
      // Error is silently handled; button stays enabled for retry
    } finally {
      setSaving(false);
    }
  }

  function handleTabSwitch(next: LegalPageType) {
    if (saving) return;
    if (next === activeTab) return;
    if (hasChanges && !globalThis.confirm("You have unsaved changes. Discard them?")) return;
    setActiveTab(next);
    setActiveLanguage("en");
  }

  const tabpanelId = `legal-page-tabpanel-${activeLanguage}`;
  const tabId = (code: LegalPageLanguage) => `legal-page-tab-${code}`;
  const editingLocked = loading || saving;

  return (
    <div className="flex flex-col h-[calc(100vh-110px)]">
      {/* Page-type segment control */}
      <div className="bg-bg-secondary rounded-full p-0.5 flex w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.type}
            onClick={() => handleTabSwitch(tab.type)}
            disabled={saving}
            className={`px-5 py-2 rounded-full text-base font-medium text-text-primary whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              activeTab === tab.type ? "bg-bg-primary" : ""
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div className="flex-1 flex flex-col gap-4 mt-5 min-h-0">
        <div className="flex items-center justify-between gap-4 shrink-0">
          <h2 className="text-base font-semibold text-text-primary">{activeLabel}</h2>
          <div
            role="tablist"
            aria-label="Language"
            className="bg-bg-secondary rounded-full p-0.5 flex w-fit"
          >
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                id={tabId(lang.code)}
                role="tab"
                aria-selected={activeLanguage === lang.code}
                aria-controls={tabpanelId}
                onClick={() => setActiveLanguage(lang.code)}
                disabled={editingLocked}
                className={`px-4 py-1.5 rounded-full text-sm font-medium text-text-primary whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  activeLanguage === lang.code ? "bg-bg-primary" : ""
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>
        <div
          id={tabpanelId}
          role="tabpanel"
          aria-labelledby={tabId(activeLanguage)}
          className="flex-1 flex min-h-0"
        >
          <textarea
            key={`${activeTab}-${activeLanguage}`}
            dir={activeLanguageMeta.dir}
            className="flex-1 border border-stroke-primary rounded-2xl p-4 text-sm leading-relaxed text-text-primary bg-bg-primary resize-none focus:outline-none focus:border-brand-brown"
            value={translations[activeLanguage]}
            onChange={(e) =>
              setTranslations((prev) => ({ ...prev, [activeLanguage]: e.target.value }))
            }
            disabled={editingLocked}
            placeholder={loading ? "Loading..." : "Enter content..."}
          />
        </div>
        {hasChanges && !canPublish && !loading && (
          <p className="text-sm text-text-secondary shrink-0">
            Both English and Arabic content are required to publish.
          </p>
        )}
      </div>

      {/* Button bar */}
      <div className="border-t border-stroke-primary pt-5 flex justify-end -mx-6 px-6 shrink-0">
        <button
          onClick={handlePublish}
          disabled={!canPublish || saving}
          className={`h-[51px] rounded-2xl px-4 font-semibold text-base text-text-btn transition-colors ${
            canPublish && !saving
              ? "bg-btn-primary cursor-pointer"
              : "bg-btn-primary-disabled cursor-not-allowed"
          }`}
        >
          {saving ? "Publishing..." : "Save and publish"}
        </button>
      </div>

      {/* Success toast */}
      {toastVisible && (
        <output className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-bg-primary rounded-2xl shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] px-4 py-3 flex items-center gap-3">
          <img src={iconCheckCircle} alt="" width={32} height={32} />
          <span className="text-sm font-medium leading-relaxed text-text-primary whitespace-nowrap">
            Changes saved and published
          </span>
        </output>
      )}
    </div>
  );
}
